import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import {
  useUsers,
  useSuspendUser,
  useResetPassword,
  useCreateAdmin,
  useCreatePlatformUser,
  useDeleteUser,
} from '@/hooks/useUsers'
import { usePlans } from '@/hooks/usePlans'
import { DataTable } from '@/components/common/DataTable'
import { SearchInput } from '@/components/common/SearchInput'
import { FilterDropdown } from '@/components/common/FilterDropdown'
import { StatusBadge } from '@/components/common/StatusBadge'
import { ConfirmDialog } from '@/components/admin/ConfirmDialog'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useHasRole } from '@/hooks/useAuth'
import { User, UsersFilter, CreateAdminRequest, CreatePlatformUserRequest } from '@/types'
import { formatDate } from '@/lib/utils'
import { Eye, UserX, UserCheck, KeyRound, Plus, Trash2 } from 'lucide-react'

const statusOptions = [
  { value: 'all', label: 'Todos' },
  { value: 'active', label: 'Activos' },
  { value: 'suspended', label: 'Suspendidos' },
]

export const UsersListPage = () => {
  const [activeTab, setActiveTab] = useState('staff')
  const [filters, setFilters] = useState<UsersFilter>({ page: 1, limit: 20 })
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [confirmAction, setConfirmAction] = useState<{
    type: 'suspend' | 'unsuspend' | 'reset' | 'delete'
    user: User
  } | null>(null)
  const [deleteEmail, setDeleteEmail] = useState('')
  const [showCreateAdmin, setShowCreateAdmin] = useState(false)
  const [createForm, setCreateForm] = useState<CreateAdminRequest>({
    email: '',
    password: '',
    name: '',
    role: 'admin',
  })
  const [showCreatePlatformUser, setShowCreatePlatformUser] = useState(false)
  const [platformForm, setPlatformForm] = useState<CreatePlatformUserRequest>({
    email: '',
    password: '',
    name: '',
    lastname: '',
    subscription_level: '',
    max_docs: undefined,
    initial_credits: undefined,
  })

  const currentFilters: UsersFilter = {
    ...filters,
    search: search || undefined,
    role: activeTab === 'staff' ? 'staff' : 'platform',
    status: statusFilter !== 'all' ? (statusFilter as 'active' | 'suspended') : undefined,
  }

  const { data, isLoading } = useUsers(currentFilters)
  const suspendUser = useSuspendUser()
  const resetPassword = useResetPassword()
  const createAdmin = useCreateAdmin()
  const createPlatformUser = useCreatePlatformUser()
  const deleteUserMutation = useDeleteUser()
  const { data: plans } = usePlans()
  const isSuperAdmin = useHasRole(['super_admin'])

  const handleSearch = (value: string) => {
    setSearch(value)
    setFilters((f) => ({ ...f, page: 1 }))
  }

  const handleTabChange = (tab: string) => {
    setActiveTab(tab)
    setSearch('')
    setStatusFilter('all')
    setFilters({ page: 1, limit: 20 })
  }

  const handleCreateAdmin = async () => {
    await createAdmin.mutateAsync(createForm)
    setShowCreateAdmin(false)
    setCreateForm({ email: '', password: '', name: '', role: 'admin' })
  }

  const handleCreatePlatformUser = async () => {
    await createPlatformUser.mutateAsync(platformForm)
    setShowCreatePlatformUser(false)
    setPlatformForm({
      email: '',
      password: '',
      name: '',
      lastname: '',
      subscription_level: '',
      max_docs: undefined,
      initial_credits: undefined,
    })
  }

  const columns = [
    {
      key: 'name',
      header: 'Nombre',
      cell: (user: User) => (
        <div>
          <div className="font-medium">
            {user.name} {user.lastname}
          </div>
          <div className="text-xs text-muted-foreground">{user.email}</div>
          {user.user_code && (
            <span className="font-mono text-[10px] text-muted-foreground">{user.user_code}</span>
          )}
        </div>
      ),
    },
    {
      key: 'role',
      header: 'Rol',
      cell: (user: User) => <StatusBadge role={user.role} />,
    },
    {
      key: 'plan',
      header: 'Plan',
      cell: (user: User) => (
        <span className="text-sm capitalize">{user.subscription_level || 'free'}</span>
      ),
    },
    {
      key: 'usage',
      header: 'Uso Docs',
      cell: (user: User) => {
        const maxDocs = user.max_docs || 0
        const pct = maxDocs > 0 ? (user.docs_used / maxDocs) * 100 : 0
        return (
          <div className="w-32">
            <div className="mb-1 flex justify-between text-xs">
              <span>
                {user.docs_used}/{maxDocs}
              </span>
              <span className="text-muted-foreground">{pct.toFixed(0)}%</span>
            </div>
            <div className="h-1.5 rounded-full bg-muted">
              <div
                className={`h-full rounded-full ${pct >= 90 ? 'bg-destructive' : pct >= 70 ? 'bg-yellow-500' : 'bg-primary'}`}
                style={{ width: `${Math.min(pct, 100)}%` }}
              />
            </div>
          </div>
        )
      },
    },
    {
      key: 'status',
      header: 'Estado',
      cell: (user: User) => <StatusBadge status={user.is_suspended ? 'suspended' : 'active'} />,
    },
    {
      key: 'created_at',
      header: 'Creado',
      cell: (user: User) => (
        <span className="text-sm text-muted-foreground">
          {user.created_at ? formatDate(user.created_at) : '-'}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      className: 'text-right',
      cell: (user: User) => (
        <div className="flex items-center justify-end gap-1">
          <Button variant="ghost" size="sm" asChild>
            <Link
              to="/users/$userId"
              params={{ userId: user.id }}
              aria-label={`Ver detalle de ${user.email}`}
              title="Ver detalle"
            >
              <Eye className="h-4 w-4" />
            </Link>
          </Button>
          <Button
            variant="ghost"
            size="sm"
            aria-label={user.is_suspended ? `Reactivar ${user.email}` : `Suspender ${user.email}`}
            title={user.is_suspended ? 'Reactivar' : 'Suspender'}
            onClick={() =>
              setConfirmAction({
                type: user.is_suspended ? 'unsuspend' : 'suspend',
                user,
              })
            }
          >
            {user.is_suspended ? (
              <UserCheck className="h-4 w-4 text-green-600" />
            ) : (
              <UserX className="h-4 w-4 text-destructive" />
            )}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            aria-label={`Restablecer contraseña de ${user.email}`}
            title="Restablecer contraseña"
            onClick={() => setConfirmAction({ type: 'reset', user })}
          >
            <KeyRound className="h-4 w-4" />
          </Button>
          {isSuperAdmin && user.role !== 'super_admin' && (
            <Button
              variant="ghost"
              size="sm"
              aria-label={`Eliminar ${user.email}`}
              title="Eliminar usuario"
              onClick={() => setConfirmAction({ type: 'delete', user })}
            >
              <Trash2 className="h-4 w-4 text-destructive" />
            </Button>
          )}
        </div>
      ),
    },
  ]

  const confirmTitle: Record<string, string> = {
    suspend: 'Suspender usuario',
    unsuspend: 'Reactivar usuario',
    reset: 'Restablecer contrasena',
    delete: 'Eliminar usuario permanentemente',
  }

  const confirmDesc: Record<string, string> = {
    suspend: `Suspender a ${confirmAction?.user.name} ${confirmAction?.user.lastname}? No podra acceder a la plataforma.`,
    unsuspend: `Reactivar a ${confirmAction?.user.name} ${confirmAction?.user.lastname}?`,
    reset: `Restablecer la contrasena de ${confirmAction?.user.name} ${confirmAction?.user.lastname}? Se generara una contrasena temporal.`,
    delete: `Eliminar permanentemente a ${confirmAction?.user.name} ${confirmAction?.user.lastname}? Esta accion es irreversible.`,
  }

  const handleConfirm = async () => {
    if (!confirmAction) return
    if (confirmAction.type === 'suspend' || confirmAction.type === 'unsuspend') {
      await suspendUser.mutateAsync({
        id: confirmAction.user.id,
        suspend: confirmAction.type === 'suspend',
      })
    } else if (confirmAction.type === 'reset') {
      await resetPassword.mutateAsync(confirmAction.user.id)
    } else if (confirmAction.type === 'delete') {
      if (deleteEmail !== confirmAction.user.email) return
      await deleteUserMutation.mutateAsync({
        id: confirmAction.user.id,
        request: { confirm_email: deleteEmail },
      })
      setDeleteEmail('')
    }
  }

  const userTable = (
    <>
      <div className="flex flex-wrap items-center gap-4">
        <SearchInput
          placeholder="Buscar por nombre o email..."
          value={search}
          onChange={handleSearch}
          className="w-72"
        />
        <FilterDropdown
          label="Estado"
          options={statusOptions}
          value={statusFilter}
          onChange={(v) => {
            setStatusFilter(v)
            setFilters((f) => ({ ...f, page: 1 }))
          }}
        />
        {activeTab === 'staff' && (
          <Button size="sm" onClick={() => setShowCreateAdmin(true)}>
            <Plus className="mr-1 h-4 w-4" />
            Crear Admin
          </Button>
        )}
        {activeTab === 'platform' && (
          <Button size="sm" onClick={() => setShowCreatePlatformUser(true)}>
            <Plus className="mr-1 h-4 w-4" />
            Crear Usuario
          </Button>
        )}
        {data && (
          <span className="ml-auto text-sm text-muted-foreground">
            {data.total} usuario{data.total !== 1 ? 's' : ''}
          </span>
        )}
      </div>

      <DataTable
        columns={columns}
        data={data?.users ?? []}
        isLoading={isLoading}
        keyExtractor={(u) => u.id}
        pagination={
          data
            ? {
                page: data.page,
                pages: data.pages,
                onPageChange: (page) => setFilters((f) => ({ ...f, page })),
              }
            : undefined
        }
        emptyMessage="No se encontraron usuarios"
      />
    </>
  )

  return (
    <div className="space-y-6">
      <h1 className="text-3xl font-bold">Usuarios</h1>

      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <TabsList>
          <TabsTrigger value="staff">Staff Admin</TabsTrigger>
          <TabsTrigger value="platform">Usuarios Plataforma</TabsTrigger>
        </TabsList>
        <TabsContent value="staff" className="space-y-4">
          {userTable}
        </TabsContent>
        <TabsContent value="platform" className="space-y-4">
          {userTable}
        </TabsContent>
      </Tabs>

      {confirmAction && confirmAction.type !== 'delete' && (
        <ConfirmDialog
          open={!!confirmAction}
          onOpenChange={(open) => !open && setConfirmAction(null)}
          title={confirmTitle[confirmAction.type]}
          description={confirmDesc[confirmAction.type]}
          confirmLabel={
            confirmAction.type === 'suspend'
              ? 'Suspender'
              : confirmAction.type === 'unsuspend'
                ? 'Reactivar'
                : 'Restablecer'
          }
          variant={confirmAction.type === 'suspend' ? 'destructive' : 'primary'}
          onConfirm={handleConfirm}
        />
      )}

      {/* Delete User Dialog */}
      <Dialog
        open={!!confirmAction && confirmAction.type === 'delete'}
        onOpenChange={(open) => {
          if (!open) {
            setConfirmAction(null)
            setDeleteEmail('')
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-destructive">Eliminar usuario permanentemente</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <p className="text-sm text-muted-foreground">
              Esta accion es <strong>irreversible</strong>. Se eliminaran todos los datos del
              usuario.
            </p>
            <div className="grid gap-2">
              <Label>
                Escribe el email para confirmar: <strong>{confirmAction?.user.email}</strong>
              </Label>
              <Input
                placeholder={confirmAction?.user.email}
                value={deleteEmail}
                onChange={(e) => setDeleteEmail(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setConfirmAction(null)
                setDeleteEmail('')
              }}
            >
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirm}
              disabled={deleteUserMutation.isPending || deleteEmail !== confirmAction?.user.email}
            >
              {deleteUserMutation.isPending ? 'Eliminando...' : 'Eliminar permanentemente'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Admin Dialog */}
      <Dialog open={showCreateAdmin} onOpenChange={setShowCreateAdmin}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Crear Admin</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label>Nombre</Label>
              <Input
                value={createForm.name}
                onChange={(e) => setCreateForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="Nombre del administrador"
                maxLength={100}
                required
              />
            </div>
            <div className="grid gap-2">
              <Label>Email</Label>
              <Input
                type="email"
                value={createForm.email}
                onChange={(e) => setCreateForm((f) => ({ ...f, email: e.target.value }))}
                placeholder="admin@uyari.io"
                maxLength={254}
                required
              />
            </div>
            <div className="grid gap-2">
              <Label>Contrasena</Label>
              <Input
                type="password"
                value={createForm.password}
                onChange={(e) => setCreateForm((f) => ({ ...f, password: e.target.value }))}
                placeholder="Minimo 8 caracteres"
                minLength={8}
                maxLength={128}
                autoComplete="new-password"
                required
              />
            </div>
            <div className="grid gap-2">
              <Label>Rol</Label>
              <Select
                value={createForm.role}
                onValueChange={(v) =>
                  setCreateForm((f) => ({
                    ...f,
                    role: v as CreateAdminRequest['role'],
                  }))
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Administrador</SelectItem>
                  <SelectItem value="finance">Finanzas</SelectItem>
                  <SelectItem value="support">Soporte</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateAdmin(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleCreateAdmin}
              disabled={
                createAdmin.isPending ||
                !createForm.email ||
                !createForm.name ||
                createForm.password.length < 8
              }
            >
              {createAdmin.isPending ? 'Creando...' : 'Crear Admin'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Platform User Dialog */}
      <Dialog open={showCreatePlatformUser} onOpenChange={setShowCreatePlatformUser}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Crear Usuario de Plataforma</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>Nombre</Label>
                <Input
                  value={platformForm.name}
                  onChange={(e) => setPlatformForm((f) => ({ ...f, name: e.target.value }))}
                  placeholder="Nombre"
                  maxLength={100}
                  required
                />
              </div>
              <div className="grid gap-2">
                <Label>Apellido</Label>
                <Input
                  value={platformForm.lastname}
                  onChange={(e) => setPlatformForm((f) => ({ ...f, lastname: e.target.value }))}
                  placeholder="Apellido"
                  maxLength={100}
                />
              </div>
            </div>
            <div className="grid gap-2">
              <Label>Email</Label>
              <Input
                type="email"
                value={platformForm.email}
                onChange={(e) => setPlatformForm((f) => ({ ...f, email: e.target.value }))}
                placeholder="usuario@ejemplo.com"
                maxLength={254}
                required
              />
            </div>
            <div className="grid gap-2">
              <Label>Contrasena</Label>
              <Input
                type="password"
                value={platformForm.password}
                onChange={(e) => setPlatformForm((f) => ({ ...f, password: e.target.value }))}
                placeholder="Minimo 8 caracteres"
                minLength={8}
                maxLength={128}
                autoComplete="new-password"
                required
              />
            </div>
            <div className="grid gap-2">
              <Label>Plan</Label>
              <Select
                value={platformForm.subscription_level}
                onValueChange={(v) => setPlatformForm((f) => ({ ...f, subscription_level: v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar plan" />
                </SelectTrigger>
                <SelectContent>
                  {plans
                    ?.filter((p) => p.is_active !== false)
                    ?.map((plan) => (
                      <SelectItem key={plan.id} value={plan.name}>
                        {plan.display_name || plan.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>Max Documentos (opcional)</Label>
                <Input
                  type="number"
                  value={platformForm.max_docs ?? ''}
                  onChange={(e) =>
                    setPlatformForm((f) => ({
                      ...f,
                      max_docs: e.target.value ? Number(e.target.value) : undefined,
                    }))
                  }
                  placeholder="Usa default del plan"
                />
              </div>
              <div className="grid gap-2">
                <Label>Creditos iniciales (opcional)</Label>
                <Input
                  type="number"
                  value={platformForm.initial_credits ?? ''}
                  onChange={(e) =>
                    setPlatformForm((f) => ({
                      ...f,
                      initial_credits: e.target.value ? Number(e.target.value) : undefined,
                    }))
                  }
                  placeholder="0"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreatePlatformUser(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleCreatePlatformUser}
              disabled={
                createPlatformUser.isPending ||
                !platformForm.email ||
                !platformForm.name ||
                !platformForm.subscription_level ||
                platformForm.password.length < 8
              }
            >
              {createPlatformUser.isPending ? 'Creando...' : 'Crear Usuario'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
